import { ExternalLink, Landmark } from "lucide-react";

export const BILL_SOURCES = [
  { code: "FED", name: "Federal", body: "Parliament of Australia", links: [
    { label: "Bills before Parliament", url: "https://www.aph.gov.au/Parliamentary_Business/Bills_Legislation/Bills_before_Parliament", note: "Every bill currently before the House or Senate." },
    { label: "Daily House & Senate Bills Lists", url: "https://www.aph.gov.au/Parliamentary_Business/Bills_Legislation/Bills_Lists", note: "Table Office lists: introduction, passage in each house, assent date and Act number." },
  ] },
  { code: "NSW", name: "New South Wales", body: "Parliament of NSW", links: [
    { label: "Current session bills", url: "https://www.parliament.nsw.gov.au/bills/pages/current-bills.aspx", note: "Each bill's origin, location, status and house." },
  ] },
  { code: "VIC", name: "Victoria", body: "Parliament of Victoria", links: [
    { label: "Bills and legislation", url: "https://www.parliament.vic.gov.au/parliamentary-activity/bills-and-legislation/", note: "Bills currently under consideration." },
    { label: "Bills in Parliament (Victorian Legislation)", url: "https://www.legislation.vic.gov.au/bills/in-parliament", note: "Bills that are before the Parliament or have been before it." },
  ] },
  { code: "QLD", name: "Queensland", body: "Queensland Parliament", links: [
    { label: "Bills and legislation", url: "https://www.parliament.qld.gov.au/Work-of-the-Assembly/Bills-and-Legislation", note: "Bills before the House and all bills this Parliament." },
    { label: "Committee inquiries", url: "https://www.parliament.qld.gov.au/work-of-committees/inquiries", note: "Current and past inquiries, submission deadlines and reporting dates." },
  ] },
  { code: "WA", name: "Western Australia", body: "Parliament of WA", links: [
    { label: "Bills progress (current Parliament)", url: "https://www.parliament.wa.gov.au/Parliament/Bills.nsf/screenBillsProgress", note: "Readings, committee stage, third reading and assent for every bill." },
  ] },
  { code: "SA", name: "South Australia", body: "SA Legislation", links: [
    { label: "Current bills", url: "https://www.legislation.sa.gov.au/legislation/bills/current", note: "All bills of the current session, including enacted, withdrawn or defeated. Links to the Legislative Tracker." },
  ] },
  { code: "TAS", name: "Tasmania", body: "Parliament of Tasmania", links: [
    { label: "Bills", url: "https://www.parliament.tas.gov.au/bills", note: "Alphabetical list of current bills and bills introduced by year." },
  ] },
  { code: "ACT", name: "Australian Capital Territory", body: "ACT Legislative Assembly", links: [
    { label: "Bills list", url: "https://www.parliament.act.gov.au/parliamentary-business/in-the-chamber/bills/bills_list", note: "Bills before the Assembly, updated each sitting." },
    { label: "ACT Legislation Register", url: "https://www.legislation.act.gov.au", note: "Full text of bills, Acts and explanatory statements." },
  ] },
  { code: "NT", name: "Northern Territory", body: "NT Legislation Portal", links: [
    { label: "Bills before the Legislative Assembly", url: "https://legislation.nt.gov.au/en/LegislationPortal/Bills/Before-Legislative-Assembly", note: "Introduced and passed bills by session." },
  ] },
];

const SourceLink = ({ l, code, i }) => (
  <a
    href={l.url}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={`bill-source-${code.toLowerCase()}-${i}`}
    className="group block rounded-lg px-3 py-2 -mx-3 hover:bg-[#1B2A41]/5 transition-colors"
  >
    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#E2705A] group-hover:text-[#c95a46]">
      {l.label} <ExternalLink className="w-3.5 h-3.5" />
    </span>
    <span className="block text-xs text-[#5C6A79] leading-relaxed mt-0.5">{l.note}</span>
  </a>
);

export const BillSourceCard = ({ s, compact = false }) => (
  <article data-testid={`bill-source-card-${s.code.toLowerCase()}`} className={`rounded-2xl bg-[#FAF8F2] border border-[#D8D2C4] ${compact ? "p-4" : "p-5"} flex flex-col`}>
    <div className="flex items-center gap-3 mb-2">
      <span className="w-10 h-10 rounded-full bg-[#1B2A41] text-[#FDFBF7] font-type font-bold text-xs flex items-center justify-center flex-shrink-0">{s.code}</span>
      <div>
        <h3 className="font-type font-bold text-sm uppercase tracking-wider text-[#1B2A41] leading-tight">{s.name}</h3>
        <p className="text-[11px] text-[#5C6A79]">{s.body}</p>
      </div>
    </div>
    <div className="space-y-1">
      {s.links.map((l, i) => <SourceLink key={l.url} l={l} code={s.code} i={i} />)}
    </div>
  </article>
);

export const BillSources = ({ codes, title = "Track current bills — official sources", intro, compact = false, testId = "bill-sources" }) => {
  const list = codes ? BILL_SOURCES.filter((s) => codes.includes(s.code)) : BILL_SOURCES;
  return (
    <section data-testid={testId}>
      <div className="flex items-start gap-3 mb-5">
        <Landmark className="w-5 h-5 text-[#E2705A] mt-1 flex-shrink-0" />
        <div>
          <h2 className="font-type font-bold text-base md:text-lg text-[#1B2A41] uppercase tracking-wider">{title}</h2>
          {intro && <p className="text-sm text-[#5C6A79] mt-1 max-w-prose leading-relaxed">{intro}</p>}
        </div>
      </div>
      <div className={`grid gap-4 ${compact ? "sm:grid-cols-2" : "sm:grid-cols-2 lg:grid-cols-3"}`}>
        {list.map((s) => <BillSourceCard key={s.code} s={s} compact={compact} />)}
      </div>
    </section>
  );
};
